"use strict";

/**
 * Plan, day redesign phase 2 (SPEC-CIS-COCKPIT-DAY-REDESIGN-P2-001 1, 2.4, 2.5):
 * the yard's forward schedule band, the later weeks and the repair-check count,
 * shaped from `forecast` and `repairChecks` of GET /api/cockpit/plan. Pure, and
 * free of any import from shape.js (shape.js imports this file).
 */

const DAY_MS = 86400000;
const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The labels of C4. A forecast item is never called "requested". */
export const FORECAST_LABEL = { expected: "Expected", likely: "Likely" };

const dayOf = (iso) => String(iso || "").slice(0, 10);

function longDay(iso) {
  const t = Date.parse(`${dayOf(iso)}T00:00:00Z`);
  if (!Number.isFinite(t)) return String(iso || "");
  const d = new Date(t);
  return `${WEEKDAY[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH[d.getUTCMonth()]}`;
}

function isoWeek(dateStr) {
  const parsed = Date.parse(`${dateStr}T00:00:00Z`);
  if (!Number.isFinite(parsed)) return null;
  const target = new Date(parsed);
  target.setUTCDate(target.getUTCDate() + 4 - (target.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  return Math.ceil(((target - yearStart) / DAY_MS + 1) / 7);
}

function weekSpan(weekStart) {
  const a = Date.parse(`${weekStart}T00:00:00Z`);
  if (!Number.isFinite(a)) return "";
  const da = new Date(a);
  const db = new Date(a + 6 * DAY_MS);
  const sameMonth = da.getUTCMonth() === db.getUTCMonth();
  const left = sameMonth ? `${da.getUTCDate()}` : `${da.getUTCDate()} ${MONTH[da.getUTCMonth()]}`;
  return `${left}–${db.getUTCDate()} ${MONTH[db.getUTCMonth()]}`;
}

/** A forecast item as the grid shows it: "UD15 P" with the inspection kind beside it. */
export function shapeForecastItem(item, date = "") {
  const side = item.side === "P" || item.side === "S" ? item.side : "";
  const block = item.block || "";
  return {
    key: `${item.code || ""}|${item.scheduleRef}`,
    code: item.code || "",
    scheduleRef: item.scheduleRef,
    date,
    title: item.title || "",
    block,
    side,
    division: item.division || "",
    confidence: item.confidence === "expected" ? "expected" : "likely",
    label: [block || item.title || item.scheduleRef, side].filter(Boolean).join(" "),
    kind: block && item.title ? item.title : item.division || "",
  };
}

/**
 * The band for the seven days on screen plus the later weeks. A day with items
 * carries one confidence (the backend labels by distance from today).
 */
export function shapeForecastBand(forecast, days) {
  const byDate = new Map(((forecast && forecast.days) || []).map((d) => [dayOf(d.date), d.items || []]));
  const cells = days.map((day) => {
    const items = (byDate.get(day.date) || []).map((it) => shapeForecastItem(it, day.date));
    const confidence = items.length ? items[0].confidence : "";
    return { date: day.date, items, count: items.length, confidence, label: confidence ? FORECAST_LABEL[confidence] : "" };
  });
  const laterWeeks = ((forecast && forecast.laterWeeks) || []).map((w) => ({
    weekStart: w.weekStart,
    label: `W${isoWeek(w.weekStart)} · ${weekSpan(w.weekStart)}`,
    sides: Number(w.sides) || 0,
    text: Number(w.sides) > 0 ? `about ${Number(w.sides)} inspections` : "None planned",
  }));
  const hasAny = cells.some((c) => c.count > 0) || laterWeeks.some((w) => w.sides > 0);
  return { cells, laterWeeks, hasAny };
}

/** Only the items of the selected projects. The weekly sums carry no project, so they are left out under a narrower selection. */
export function filterPlanForecast(forecast, selectedCodes, allCodes = []) {
  if (!forecast) return forecast;
  if (!selectedCodes || !selectedCodes.length) return forecast;
  const narrowed = allCodes.length > 0 && selectedCodes.length < allCodes.length;
  return {
    ...forecast,
    days: (forecast.days || []).map((d) => ({ ...d, items: (d.items || []).filter((i) => !i.code || selectedCodes.includes(i.code)) })),
    laterWeeks: narrowed ? [] : forecast.laterWeeks || [],
  };
}

export function filterPlanRepairChecks(repairChecks, selectedCodes) {
  if (!repairChecks) return repairChecks;
  if (!selectedCodes || !selectedCodes.length) return repairChecks;
  const items = (repairChecks.items || []).filter((i) => !i.code || selectedCodes.includes(i.code));
  return { ...repairChecks, count: items.length, items };
}

/** "Waiting for a repair check · N": oldest first, whatever order the payload came in. */
export function shapeRepairChecks(repairChecks) {
  const items = ((repairChecks && repairChecks.items) || [])
    .map((i) => {
      const block = i.block || "";
      const side = i.side === "P" || i.side === "S" ? i.side : "";
      const where = [block || i.title || "Record", side].filter(Boolean).join(" ");
      return {
        key: `${i.code || ""}|${i.inspectionNumber}`,
        code: i.code || "",
        inspectionNumber: i.inspectionNumber,
        inspectionDate: dayOf(i.inspectionDate),
        where,
        title: i.title || "",
        defectText: i.defectText || "",
        dateText: longDay(i.inspectionDate),
        line: `${where}, our inspection ${longDay(i.inspectionDate)}`,
      };
    })
    .sort((a, b) => a.inspectionDate.localeCompare(b.inspectionDate) || Number(a.inspectionNumber) - Number(b.inspectionNumber));
  const count = items.length;
  return {
    count,
    items,
    oldestText: count ? `Oldest: ${items[0].line}.` : "",
    heading: `Waiting for a repair check · ${count}`,
  };
}

/**
 * The upsert that books a forecast item (2.5): the stored row, with only the
 * date and the assignee changed and `basis: ""` (the explicit clear). Nothing
 * else about the row is sent differently, so the server's import-field check
 * passes.
 */
export function buildForecastBookRow(stored, { assignedTo, plannedDate }) {
  const row = {
    scheduleRef: stored.scheduleRef,
    title: stored.title,
    category: stored.category,
    stage: stored.stage,
    inspectionType: stored.inspectionType,
    plannedDate,
    witnessParties: stored.witnessParties || "",
    assignedTo: assignedTo || "",
    notes: stored.notes || "",
    basis: "",
  };
  if (stored.sourceInspectionNumber) row.sourceInspectionNumber = stored.sourceInspectionNumber;
  return row;
}

export function forecastDragId(item) {
  return `forecast:${item.key}`;
}
